import { Button, CardProps, Stack, Text } from '@chakra-ui/react'
import { useCMAStore } from 'features/CMA/infra/react/CMAState'
import ConsumableAttempt from 'features/common/ConsumableAttempt/domain/ConsumableAttempt'
import { ConsumableAttemptBillingSummaryCard } from 'features/common/ConsumableAttempt/views/ConsumableAttemptBillingSummaryCard'
import { DateLib } from 'libs/Date'
import { Card } from 'presentation/components/molecules/Card'
import { ResponsiveFullscreenModal } from 'presentation/components/ResponsiveFullscreenModal'
import { useLeadId } from 'presentation/libs/LeadIdContext'
import { LegacyBillingEvents } from 'presentation/screens/Billing/Billing.events'
import { mbp } from 'presentation/utils/mapBreakpoint'
import { useEffect, useRef, useState } from 'react'
import { useHasMoreCompsPlans } from '../../hooks/useHasMoreCompsPlans'

export type CompsInitializeModalProps = {
  isColumnCard?: boolean
  onCancel?: () => void
  isOpen?: boolean
} & CardProps

export const CompsInitializeModal = ({ onCancel, isOpen = true }: CompsInitializeModalProps) => {
  const state = useCompsInitializeModalState()
  const leadId = useLeadId()
  const initializeReport = useCMAStore(state => state.actions.initializeLoadReport.execute)
  const { hasMoreCompsPlans, loading: plansLoading } = useHasMoreCompsPlans()

  // State to control which modal to show
  const [showOutOfFreeCompsModal, setShowOutOfFreeCompsModal] = useState(false)
  const [showPaymentModal, setShowPaymentModal] = useState(false)

  // Use a ref to track whether the user has chosen to pay
  const userChoseToPay = useRef(false)

  // Track if this is the initial mount
  const didInitialSetup = useRef(false)

  // Track previous isOpen value to detect changes
  const prevIsOpenRef = useRef(isOpen)

  // Use the onCancel prop if provided, otherwise use empty function
  const onClose = onCancel || (() => {})

  // Reset all state when modal is reopened
  useEffect(() => {
    // If modal was closed and is now open again
    if (!prevIsOpenRef.current && isOpen) {
      // Reset all refs and state
      userChoseToPay.current = false
      didInitialSetup.current = false
      setShowOutOfFreeCompsModal(false)
      setShowPaymentModal(false)
    }

    // Update the previous value
    prevIsOpenRef.current = isOpen
  }, [isOpen])

  // Set up event listener for when funds are added
  useEffect(() => {
    if (!leadId) return

    const handleAddFundsDone = () => {
      // Only call initializeReport if we have a valid leadId and state is ready
      if (leadId)
        void initializeReport(leadId)
    }

    // Subscribe to the event and store the emitter
    const emitter = LegacyBillingEvents.addFundsDone.subscribe(handleAddFundsDone)

    // Clean up the subscription when component unmounts
    return () => {
      emitter.off('addFundsDone', handleAddFundsDone)
    }
  }, [leadId, initializeReport])

  // Determine which modal to show based on comp status - only runs once when ready
  useEffect(() => {
    // Only run this once when state becomes ready and we haven't done setup yet
    // Also ensure the plans data has loaded
    if (state.status === 'ready' && !didInitialSetup.current && !userChoseToPay.current && !plansLoading) {
      didInitialSetup.current = true

      if (state.consumableInfo.status === 'sufficient-consumables') {
        // Auto-confirm and skip modal if free comps are available
        state.confirmLoadReport()
        onClose()
      } else if (hasMoreCompsPlans) {
        // Show the out of free comps modal if there are plans with more comps
        setShowOutOfFreeCompsModal(true)
        setShowPaymentModal(false)
      } else {
        // No better plans available, show payment modal directly
        setShowOutOfFreeCompsModal(false)
        setShowPaymentModal(true)
      }
    }
  }, [state, hasMoreCompsPlans, onClose, plansLoading])

  // Handler for when user chooses to pay for comp from the OutOfFreeCompsModal
  const handleProceedToPayment = () => {
    // Set flag that user has chosen to pay
    userChoseToPay.current = true
    setShowOutOfFreeCompsModal(false)
    setShowPaymentModal(true)
  }

  // Handler for when user chooses to upgrade their plan
  const handleUpgrade = () => {
    // Navigate to the pricing page with comps section
    window.location.href = '/pricing#comps'
    onClose()
  }

  if (state.status === 'unready' || plansLoading) return null

  // If we have free comps, don't show any modal (it will be auto-confirmed by the effect above)
  if (state.status === 'ready' && state.consumableInfo.status === 'sufficient-consumables')
    return null

  // Show the out of free comps modal first
  if (showOutOfFreeCompsModal && state.status === 'ready') {
    const currentPlanName = state.consumableInfo.planName

    return (
      <ResponsiveFullscreenModal
        isOpen={isOpen}
        onClose={onClose}
        title='Out of Free Comps'
      >
        <Card
          variant='default'
          colorScheme='modal.highlight'
          size={mbp({
            mobSm: 'xs-locked',
            mob: 'sm-locked',
            tabSm: 'lg-locked',
            dtLg: 'lg-locked',
          })}
          maxW='736px'
        >
          <Text
            textStyle='h2'
            color='graystrong.500'
          >
            Out of Free Comps
          </Text>

          <Text
            mt={mbp({ mobSm: 2, mob: 3, tabSm: 2, dtLg: 2 })}
            textStyle='bodyXLFat'
            color='graystrong.200'
          >
            You&apos;ve run out of free comps for this month on your
            {' '}
            {currentPlanName}
            {' '}
            plan.
          </Text>

          <Text
            mt={2}
            textStyle='bodyL'
            color='graystrong.200'
          >
            You can either pay for individual comps or upgrade your plan for additional free comps each month.
          </Text>

          <Stack
            justifyContent='center'
            gap={3}
            mt={mbp({ mobSm: 3, mob: 4, tabSm: 5 })}
            direction={{
              base: 'column',
              tabSm: 'row',
            }}
          >
            <Button
              variant='outline'
              colorScheme='neutral'
              size='lg'
              onClick={onClose}
              order={{
                base: '3',
                tabSm: '1',
              }}
            >
              Cancel
            </Button>
            <Button
              variant='outline'
              colorScheme='positive'
              size='lg'
              onClick={handleProceedToPayment}
              order={{
                base: '2',
                tabSm: '2',
              }}
            >
              Pay for Comp
            </Button>
            <Button
              w={mbp({ mobSm: 'auto', mob: 'auto' })}
              variant='solid'
              colorScheme='positive'
              size='lg'
              onClick={handleUpgrade}
              order={{
                base: '1',
                tabSm: '3',
              }}
            >
              Upgrade Plan
            </Button>
          </Stack>
        </Card>
      </ResponsiveFullscreenModal>
    )
  }

  // Show the regular payment modal
  if (showPaymentModal && state.status === 'ready') {
    return (
      <ResponsiveFullscreenModal
        isOpen={isOpen}
        onClose={onClose}
        title='Pay for this Comp Report'
      >
        <CompsInitializeCard
          maxW='736px'
          onCancel={onClose}
          state={state}
        />
      </ResponsiveFullscreenModal>
    )
  }

  return null
}

type CompsInitializeCardProps = {
  state: ReturnType<typeof useCompsInitializeModalState> & { status: 'ready' }
  isColumnCard?: boolean
  onCancel?: () => void
} & CardProps

const CompsInitializeCard = (props: CompsInitializeCardProps) => {
  const {
    state,
    onCancel,
    isColumnCard,
    ...cardProps
  } = props

  const availableFunds = state.consumableInfo.funds.available
  const pricePenniesPerUnit = state.consumableInfo.price
  const nextRenewalDateRaw = state.consumableInfo.consumablesRefreshDate
  const nextRenewalDate = DateLib.formatMMDDYYDots(nextRenewalDateRaw)

  return (
    <ConsumableAttemptBillingSummaryCard
      title='Comp Report'
      subtitle=''
      consumableType='comps'
      paymentPrice={pricePenniesPerUnit}
      pricePenniesPerUnit={pricePenniesPerUnit}
      refreshingConsumables={state.consumableInfo.refreshingConsumables}
      remainingConsumables={state.consumableInfo.remainingConsumables}
      consumablesRefreshDate={nextRenewalDate}
      currentPlan={state.consumableInfo.planName}
      freeItemExceedCount={undefined}
      availableFunds={availableFunds}
      fundsStatus={state.consumableInfo.status}
      onAttempt={state.confirmLoadReport}
      isProcessing={false}
      onCancel={onCancel}
      isColumnCard={isColumnCard}
      upgradeUrl='/pricing#comps'
      quantity={1}
      {...cardProps}
    />
  )
}

const useCompsInitializeModalState = (): {
  status: 'ready'
  consumableInfo: ConsumableAttempt
  confirmLoadReport: () => void
} | {
  status: 'unready'
} => {
  const leadId = useLeadId()

  return useCMAStore(state => {
    const reportState = state.local.report
    const loadReport = state.actions.loadReport.execute

    if (!leadId || reportState.status !== 'initiated') return { status: 'unready' as const }

    return {
      status: 'ready' as const,
      consumableInfo: reportState.consumablesInfo,
      confirmLoadReport: () => void loadReport({
        id: leadId,
        confirmationKey: reportState.consumablesInfo.confirmationKey,
      }),
    }
  })
}
